// frontend/src/services/api.js
import axios from 'axios';
import toast from 'react-hot-toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Helper to read a cookie by name
function getCookie(name) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

// Create the main API instance
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Send cookies for CSRF
});

export default api;

// Request interceptor to add auth token, organization context, and CSRF token
api.interceptors.request.use(
  (config) => {
    // Add auth token
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add CSRF token from cookie (Double-Submit Cookie pattern)
    const csrfToken = getCookie('XSRF-TOKEN');
    if (csrfToken) {
      config.headers['X-XSRF-TOKEN'] = csrfToken;
    }

    // Add organization context header
    const organizationId = localStorage.getItem('currentOrganizationId');
    if (organizationId) {
      config.headers['X-Organization-Id'] = organizationId;
    }
    
    // Check if data is FormData
    const isFormData = config.data instanceof FormData;
    
    if (isFormData) {
      // For FormData, ensure no Content-Type is set
      // The browser will automatically set multipart/form-data with boundary
      delete config.headers['Content-Type'];
      
      // Only delete from common if it exists
      if (config.headers.common) {
        delete config.headers.common['Content-Type'];
      }
      
      // Debug log (development only)
      if (process.env.NODE_ENV === 'development') {
        console.log('Sending FormData request:', {
          method: config.method,
          url: config.url,
        });
      }
    } else {
      // For non-FormData requests, set JSON content type if not already set
      if (!config.headers['Content-Type']) {
        config.headers['Content-Type'] = 'application/json';
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      // Log full error details in development
      if (process.env.NODE_ENV === 'development') {
        console.error('API Error:', {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers,
          config: error.config
        });
      }
      
      // Handle specific error cases
      const data = error.response.data || {};
      switch (error.response.status) {
        case 401:
          // Unauthorized - redirect to login
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          localStorage.removeItem('currentOrganizationId');
          window.location.href = '/login';
          break;
        case 402: {
          // Tier upgrade required — backend's global error middleware sends
          // `currentTier`, `featureKey`, `requiredTiers` alongside the message
          // (see app.js error handler). Surface a rich toast so users know
          // WHY they were blocked and what plan unlocks it.
          const tiers = Array.isArray(data.requiredTiers) && data.requiredTiers.length
            ? data.requiredTiers.join(' / ')
            : null;
          const upgradeMsg = data.error
            || (tiers ? `This action requires ${tiers}` : 'Upgrade required to use this feature');
          toast.error(upgradeMsg + (data.currentTier ? ` (current: ${data.currentTier})` : ''));
          break;
        }
        case 403:
          // Don't show generic toast if the response has upgradeRequired flag
          // (callers handle those with specific messages)
          if (!data.upgradeRequired) {
            toast.error(data.error || 'You do not have permission to perform this action');
          }
          break;
        case 404:
          // Don't show error for 404s in development when API is not running
          if (process.env.NODE_ENV !== 'development') {
            toast.error(data.error || 'Resource not found');
          }
          break;
        case 429:
          // Rate limited — show the retry-after when the backend sends it
          // (Express rate-limit middleware sets it in the header).
          {
            const retryAfter = error.response.headers?.['retry-after'];
            const suffix = retryAfter ? ` (retry in ${retryAfter}s)` : '';
            toast.error((data.error || 'Too many requests') + suffix);
          }
          break;
        case 500:
        case 502:
        case 503:
        case 504:
          // Surface the backend's error message when it sent one (helps
          // support diagnose faster than generic "server error"). Fall back
          // to generic when backend leaked nothing useful.
          toast.error(data.error || data.message || `Server error (${error.response.status}). Please try again.`);
          break;
        default:
          // Validation errors + anything else — always try to surface
          // SOMETHING so a swallowed non-2xx doesn't leave the user
          // wondering why the button didn't work.
          if (data.error) {
            toast.error(data.error);
          } else if (data.message) {
            toast.error(data.message);
          } else if (Array.isArray(data.details) && data.details.length > 0) {
            toast.error(data.details.map(d => d.message).join(', '));
          } else {
            // Unknown shape — surface the status code at minimum so the
            // failure isn't invisible. Console-log the raw response for
            // engineering follow-up.
            console.warn('API returned unhandled error shape', { status: error.response.status, data });
            toast.error(`Request failed (HTTP ${error.response.status})`);
          }
      }
    } else if (error.request) {
      // Network error / no response — always log to console; skip toast
      // only in dev to keep local iteration quiet.
      console.error('Network error:', error?.message, error);
      if (process.env.NODE_ENV !== 'development') {
        toast.error('Network error. Please check your connection.');
      }
    } else {
      // Setup-time error (bad axios config, canceled request without a
      // response, etc.). Was previously invisible.
      console.error('Axios setup error:', error?.message, error);
    }
    
    return Promise.reject(error);
  }
);

// Auth API endpoints
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/users/profile', data),
  changePassword: (passwords) => api.put('/auth/change-password', passwords),
  refreshToken: (refreshToken) => api.post('/auth/refresh', { refreshToken }),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
  verifyEmail: (token) => api.post('/auth/verify-email', { token }),
  resendVerification: (email) => api.post('/auth/resend-verification', { email }),
  // Public signup (creates user + organization)
  signup: (userData) => api.post('/auth/signup', userData),
  checkEmailAvailability: (email) => api.get('/auth/check-email', { params: { email } })
};

// Users API endpoints
export const usersAPI = {
  getUsers: (params) => api.get('/users', { params }),
  getUser: (id) => api.get(`/users/${id}`),
  createUser: (data) => api.post('/auth/register', data),
  updateUser: (id, data) => api.put(`/users/${id}`, data),
  deleteUser: (id) => api.delete(`/users/${id}`),
  resetUserPassword: (id, password) => api.put(`/users/${id}/reset-password`, { password }),
  
  // User statistics and review performance
  getUserStats: (id) => api.get(`/users/${id}/stats`),
  getUserReviewStats: (id, params) => api.get(`/users/${id}/review-stats`, { params }),
  getUserPerformance: (id, params) => api.get(`/users/${id}/performance`, { params }),
  getReviewerLeaderboard: (params) => api.get('/users/reviewer-leaderboard', { params })
};

// Language Pairs API endpoints - Add this section to your api.js file
export const languagePairsAPI = {
  getLanguages: () => api.get('/language-pairs/languages'),
  getIndustries: () => api.get('/language-pairs/industries'),
  getUserLanguagePairs: (userId) => api.get(`/language-pairs/users/${userId}`),
  addUserLanguagePair: (userId, data) => {
    return api.post(`/language-pairs/users/${userId}`, data);
  },
  updateLanguagePair: (id, data) => api.put(`/language-pairs/${id}`, data),
  deleteLanguagePair: (id) => api.delete(`/language-pairs/${id}`),
  findReviewers: (data) => api.post('/language-pairs/find-reviewers', data),
};

// Analytics API endpoints - UPDATED
export const analyticsAPI = {
  getDashboardStats: (params) => api.get('/analytics/dashboard', { params }), // FIXED: Now accepts params
  getProjectStats: (projectId) => api.get(`/analytics/projects/${projectId}`),
  getUserStats: (userId) => api.get(`/analytics/users/${userId}`),
  getProductivityReport: (params) => api.get('/analytics/productivity', { params }),
  getLanguageStats: (params) => api.get('/analytics/languages', { params }),
  getLeaderboard: (params) => api.get('/analytics/leaderboard', { params }),
  getTMAnalytics: () => api.get('/analytics/translation-memories'),
  getCostAnalytics: (params) => api.get('/analytics/costs', { params }),
  exportAnalytics: (data) => api.post('/analytics/export', data, { responseType: 'blob' }),
  // Sprint 13: Pipeline health dashboard
  getPipelineHealth: (params) => api.get('/analytics/pipeline-health', { params }),
  // Wave-03 · QE trend + regression detection
  getQeTrend: (projectId, params) => api.get(`/analytics/qe-trend/project/${projectId}`, { params }),
  getQeRegressions: (params) => api.get('/analytics/qe-regressions', { params }),
  getLlmFallbackStats: () => api.get('/analytics/llm-fallback-stats'),
  // Wave-04 · QE feedback-loop auto-tune
  getQeAutoTune: (projectId, params) => api.get(`/analytics/qe-auto-tune/project/${projectId}`, { params }),
  getQeTuningSuggestions: (params) => api.get('/analytics/qe-auto-tune', { params }),
  // Wave-06 · Review SLA breach dashboard
  getReviewSla: () => api.get('/analytics/review-sla'),
  // Wave-16 · Edit-distance summary per project (cached metric on Segment).
  getEditDistance: (projectId) => api.get(`/analytics/edit-distance/project/${projectId}`),
};

// Wave-05 · audit log dashboard
export const auditLogAPI = {
  list: (params) => api.get('/analytics/audit-log', { params }),
  facets: () => api.get('/analytics/audit-log/facets'),
  exportCsv: (params) => api.get('/analytics/audit-log/export', { params, responseType: 'blob' }),
};

// Wave-04 · admin-managed domain / industry catalog
export const domainsAPI = {
  list: (params) => api.get('/domains', { params }),
  create: (data) => api.post('/domains', data),
  update: (id, data) => api.put(`/domains/${id}`, data),
  remove: (id) => api.delete(`/domains/${id}`),
};

// Wave-05 · SSO (OIDC) — public discovery + admin config
export const ssoAPI = {
  discover: (email) => api.get('/auth/sso/discover', { params: { email } }),
  startUrl: (orgId) => `/api/auth/sso/start?orgId=${encodeURIComponent(orgId)}`,
  getConfig: (orgId) => api.get(`/organizations/sso/config/${orgId}`),
  updateConfig: (orgId, data) => api.put(`/organizations/sso/config/${orgId}`, data),
  testConfig: (orgId) => api.post(`/organizations/sso/config/${orgId}/test`),
};

// Wave-05 · PII scan endpoints
export const piiScanAPI = {
  scanTm: (id, body) => api.post(`/pii-scan/tm/${id}`, body || {}),
  scanGlossary: (id, body) => api.post(`/pii-scan/glossary/${id}`, body || {}),
  scanOrg: (body) => api.post('/pii-scan/org', body || {}),
  redactTmSegment: (id, body) => api.post(`/pii-scan/tm-segment/${id}/redact`, body || {}),
};

// Sprint 11 — A/B pipeline experiments admin
export const experimentsAPI = {
  list: () => api.get('/experiments'),
  get: (id) => api.get(`/experiments/${id}`),
  create: (body) => api.post('/experiments', body),
  start: (id) => api.post(`/experiments/${id}/start`),
  pause: (id) => api.post(`/experiments/${id}/pause`),
  complete: (id) => api.post(`/experiments/${id}/complete`),
  delete: (id) => api.delete(`/experiments/${id}`),
  results: (id) => api.get(`/experiments/${id}/results`),
};

// Sprint 12 — Per-account fine-tuning admin
export const fineTunesAPI = {
  list: () => api.get('/fine-tunes'),
  preview: (params) => api.get('/fine-tunes/preview', { params }),
  get: (id) => api.get(`/fine-tunes/${id}`),
  create: (body) => api.post('/fine-tunes', body),
  refresh: (id) => api.post(`/fine-tunes/${id}/refresh`),
  promote: (id, body) => api.post(`/fine-tunes/${id}/promote`, body || {}),
  archive: (id) => api.post(`/fine-tunes/${id}/archive`),
};

// Sprint 18 — Per-org PII policy default
export const orgPolicyAPI = {
  getPiiPolicy: (orgId) => api.get(`/organizations/${orgId}/pii-policy`),
  setPiiPolicy: (orgId, body) => api.put(`/organizations/${orgId}/pii-policy`, body),
};

// Projects API endpoints
export const projectsAPI = {
  getProjects: (params) => api.get('/projects', { params }),
  getProject: (id) => api.get(`/projects/${id}`),
  createProject: (data) => api.post('/projects', data),
  updateProject: (id, data) => api.put(`/projects/${id}`, data),
  deleteProject: (id) => api.delete(`/projects/${id}`),
  startTranslation: (id) => api.post(`/projects/${id}/translate`),
  assignReviewers: (id, data) => api.post(`/projects/${id}/assign-reviewers`, data),
  getProjectActivities: (id) => api.get(`/projects/${id}/activities`),
  getFileProgress: (id) => api.get(`/projects/${id}/file-progress`),
};

// Translation Memory API endpoints
export const translationMemoryAPI = {
  getMemories: (params) => api.get('/translation-memories', { params }),
  getMemory: (id) => api.get(`/translation-memories/${id}`),
  createMemory: (data) => api.post('/translation-memories', data),
  updateMemory: (id, data) => api.put(`/translation-memories/${id}`, data),
  deleteMemory: (id) => api.delete(`/translation-memories/${id}`),
  uploadTMX: (formData) => api.post('/translation-memories/upload', formData),
  searchSegments: (data) => api.post('/translation-memories/search', data),
  getSegments: (tmId, params) => api.get(`/translation-memories/${tmId}/segments`, { params }),
  addSegment: (tmId, data) => api.post(`/translation-memories/${tmId}/segments`, data),
  updateSegment: (tmId, segmentId, data) => api.put(`/translation-memories/${tmId}/segments/${segmentId}`, data),
  deleteSegment: (tmId, segmentId) => api.delete(`/translation-memories/${tmId}/segments/${segmentId}`),
};

// Glossary API endpoints
export const glossaryAPI = {
  // List glossaries
  getGlossaries: (params) => api.get('/glossaries', { params }),
  
  // Get single glossary
  getGlossary: (id) => api.get(`/glossaries/${id}`),
  
  // Create empty glossary (JSON body — no file upload).
  // Backend route is /glossaries/blank; multer sits on /glossaries and
  // would reject a plain JSON body with "Unexpected end of form".
  createGlossary: (data) => api.post('/glossaries/blank', data),

  // Update glossary
  updateGlossary: (id, data) => api.put(`/glossaries/${id}`, data),
  
  // Delete glossary
  deleteGlossary: (id) => api.delete(`/glossaries/${id}`),
  
  // Upload glossary file - THIS IS THE MAIN UPLOAD ENDPOINT
  uploadGlossary: (formData) => api.post('/glossaries', formData),
  
  // Download glossary
  downloadGlossary: (id) => api.get(`/glossaries/${id}/download`, {
    responseType: 'blob', // Important for file downloads
  }),
  
  // Term management
  getGlossaryTerms: (glossaryId, params) => api.get(`/glossaries/${glossaryId}/terms`, { params }),
  
  // Add term - only send fields expected by backend for new terms
  addTerm: (glossaryId, data) => {
    // For new terms, only send the fields that backend validation expects
    const cleanData = {
      sourceTerm: data.sourceTerm,
      targetTerm: data.targetTerm,
      caseSensitive: Boolean(data.caseSensitive),
    };
    
    // Only include note if it has a value
    if (data.note && data.note.trim()) {
      cleanData.note = data.note.trim();
    }
    
    return api.post(`/glossaries/${glossaryId}/terms`, cleanData);
  },
  
  // Update term - can include approved field
  updateTerm: (glossaryId, termId, data) => {
    // For updates, we can include all fields
    const cleanData = {
      sourceTerm: data.sourceTerm,
      targetTerm: data.targetTerm,
      caseSensitive: Boolean(data.caseSensitive),
    };
    
    // Only include note if it has a value
    if (data.note && data.note.trim()) {
      cleanData.note = data.note.trim();
    }
    
    // Include approved for updates
    if (data.approved !== undefined) {
      cleanData.approved = Boolean(data.approved);
    }
    
    return api.put(`/glossaries/${glossaryId}/terms/${termId}`, cleanData);
  },
  
  deleteTerm: (glossaryId, termId) => api.delete(`/glossaries/${glossaryId}/terms/${termId}`),
  
  // Search and find
  searchTerms: (data) => api.post('/glossaries/search', data),
  findTermsInText: (data) => api.post('/glossaries/find-terms', data),
};

// Files API endpoints
export const filesAPI = {
  uploadFile: (projectId, formData, config = {}) => api.post(`/projects/${projectId}/files`, formData, config),
  uploadMultipleFiles: (projectId, formData) => api.post(`/projects/${projectId}/files`, formData),
  getFile: (id) => api.get(`/files/${id}`),
  downloadFile: (id) => api.get(`/files/${id}/download`, { responseType: 'blob' }),
  deleteFile: (id) => api.delete(`/files/${id}`),
  // Correct RESTful path — the /files/:id above targets a route that doesn't
  // exist on the backend. Use deleteProjectFile(projectId, fileId) for a real
  // hard-delete via /api/projects/:projectId/files/:fileId.
  deleteProjectFile: (projectId, fileId) => api.delete(`/projects/${projectId}/files/${fileId}`),
  getFileSegments: (fileId, params) => api.get(`/files/${fileId}/segments`, { params }),
  parseFile: (id) => api.post(`/files/${id}/parse`),
};

// MT Configuration API endpoints - Using fetch for FormData
export const mtConfigAPI = {
  // Get available MT engines for project selection (public - any authenticated user)
  getAvailableEngines: () => api.get('/mt-configs/available-engines'),

  // List configurations (admin only)
  getConfigurations: (params) => api.get('/mt-configs', { params }),

  // Get active configurations for project selection (accessible to project managers)
  getActiveConfigurations: (params) => api.get('/mt-configs/active', { params }),

  // Get platform AutoML models for project selection (accessible to any authenticated user)
  getPlatformAutoMLModels: () => api.get('/mt-configs/platform-automl-models'),

  // Get single configuration
  getConfiguration: (id) => api.get(`/mt-configs/${id}`),

  // Get available models for a configuration
  getAvailableModels: (id) => api.get(`/mt-configs/${id}/models`),

  // Get standard engine configuration status
  getEngineStatus: () => api.get('/mt-configs/engine-status'),

  // Update configuration - Using native fetch for proper FormData handling
  updateConfiguration: async (id, formData) => {
    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch(`${API_BASE_URL}/mt-configs/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          // Don't set Content-Type - let browser handle it for FormData
        },
        body: formData,
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw {
          response: {
            status: response.status,
            data: data,
          }
        };
      }
      
      return { data };
    } catch (error) {
      throw error;
    }
  },

  // Create configuration - Using native fetch for proper FormData handling
  createConfiguration: async (formData) => {
    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch(`${API_BASE_URL}/mt-configs`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          // Don't set Content-Type - let browser handle it for FormData
        },
        body: formData,
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw {
          response: {
            status: response.status,
            data: data,
          }
        };
      }
      
      return { data };
    } catch (error) {
      throw error;
    }
  },
  
  // Delete configuration
  deleteConfiguration: (id) => api.delete(`/mt-configs/${id}`),
  
  // Test configuration
  testConfiguration: (id, testData) => api.post(`/mt-configs/${id}/test`, testData),
  
  // Set as default
  setDefault: (id) => api.put(`/mt-configs/${id}/set-default`),
};

// Activities API endpoints
export const activitiesAPI = {
  // Get user activities
  getUserActivities: (userId, params) => api.get(`/activities/users/${userId}`, { params }),
  
  // Get user activity statistics
  getUserActivityStats: (userId, days = 30) => api.get(`/activities/users/${userId}/stats`, { params: { days } }),
  
  // Get all activities (admin only)
  getAllActivities: (params) => api.get('/activities', { params }),
};

// Review Tracking API endpoints - NEW SECTION
export const reviewAPI = {
  // Session Management
  startReviewSession: (projectId) => 
    api.post('/review/sessions/start', { projectId }),
  
  endReviewSession: (sessionId) => 
    api.post('/review/sessions/end', { sessionId }),
  
  getActiveSessions: () => 
    api.get('/review/sessions/active'),
  
  getSessionHistory: (params) => 
    api.get('/review/sessions/history', { params }),
  
  getSessionStats: (sessionId) => 
    api.get(`/review/sessions/${sessionId}/stats`),
  
  // Segment Review Tracking
  startSegmentReview: (segmentId) => 
    api.post(`/segments/${segmentId}/review/start`),
  
  pauseSegmentReview: (segmentId) => 
    api.post(`/segments/${segmentId}/review/pause`),
  
  completeSegmentReview: (segmentId, data) => 
    api.post(`/segments/${segmentId}/review/complete`, data),
  
  getSegmentReviewStats: (segmentId) => 
    api.get(`/segments/${segmentId}/review/stats`),
  
  // Segment Updates (used in conjunction with review tracking)
  updateSegment: (segmentId, data) => 
    api.put(`/translations/segments/${segmentId}`, data),
  
  // Correction Tracking (for quality score)
  markSegmentCorrected: (segmentId, data) => 
    api.post(`/segments/${segmentId}/corrections`, data),
  
  getSegmentCorrections: (segmentId) => 
    api.get(`/segments/${segmentId}/corrections`),
  
  // Admin Functions
  checkIdleSessions: () => 
    api.post('/review/sessions/check-idle'),
  
  cleanupStaleSessions: () => 
    api.post('/review/sessions/cleanup'),
  
  // Reviewer Performance
  getReviewerPerformance: (reviewerId, params) => 
    api.get(`/review/reviewers/${reviewerId}/performance`, { params }),
  
  getReviewerDailyStats: (reviewerId, params) => 
    api.get(`/review/reviewers/${reviewerId}/daily-stats`, { params }),
  
  // Project Review Stats
  getProjectReviewStats: (projectId) => 
    api.get(`/review/projects/${projectId}/stats`),
  
  getProjectReviewerStats: (projectId) => 
    api.get(`/review/projects/${projectId}/reviewer-stats`)
};

// Segments API endpoints - NEW SECTION
export const segmentsAPI = {
  // Basic CRUD
  getSegments: (params) => api.get('/segments', { params }),
  getSegment: (id) => api.get(`/segments/${id}`),
  updateSegment: (id, data) => api.put(`/segments/${id}`, data),
  deleteSegment: (id) => api.delete(`/segments/${id}`),
  
  // Bulk operations
  bulkUpdateSegments: (data) => api.post('/segments/bulk-update', data),
  bulkDeleteSegments: (ids) => api.post('/segments/bulk-delete', { ids }),
  
  // Project segments — the backend mounts the list route under the
  // translations router, not the projects router. Wave-03 surfaced this
  // via a 404 when the Semantic TM Preview page tried to pull a sample
  // segment. Corrected here so every caller benefits.
  getProjectSegments: (projectId, params) =>
    api.get(`/translations/projects/${projectId}/segments`, { params }),
  
  // File segments
  getFileSegments: (fileId, params) => 
    api.get(`/files/${fileId}/segments`, { params }),
  
  // Review operations (duplicated for convenience)
  startReview: (segmentId) => api.post(`/segments/${segmentId}/review/start`),
  pauseReview: (segmentId) => api.post(`/segments/${segmentId}/review/pause`),
  completeReview: (segmentId, data) => api.post(`/segments/${segmentId}/review/complete`, data),
  
  // Context and history
  getSegmentContext: (segmentId, size = 2) => 
    api.get(`/segments/${segmentId}/context`, { params: { size } }),
  
  getSegmentHistory: (segmentId) => 
    api.get(`/segments/${segmentId}/history`),
  
  // Comments
  addComment: (segmentId, comment) => 
    api.post(`/segments/${segmentId}/comments`, { comment }),
  
  getComments: (segmentId) => 
    api.get(`/segments/${segmentId}/comments`),
  
  deleteComment: (segmentId, commentId) => 
    api.delete(`/segments/${segmentId}/comments/${commentId}`),
  
  // Quality and corrections
  markAsIncorrect: (segmentId, reason) => 
    api.post(`/segments/${segmentId}/mark-incorrect`, { reason }),
  
  approveSegment: (segmentId) => 
    api.post(`/segments/${segmentId}/approve`),
  
  rejectSegment: (segmentId, reason) => 
    api.post(`/segments/${segmentId}/reject`, { reason })
};

// MT Usage API endpoints - NEW ADDITION
export const mtUsageAPI = {
  // User endpoints (for PMs)
  getCurrentUsage: () => {
    return api.get('/mt/usage/current');
  },
  
  getUsageHistory: (months = 6) => {
    return api.get('/mt/usage/history', {
      params: { months }
    });
  },
  
  // Admin endpoints
  getAllUserLimits: (params = {}) => {
    return api.get('/mt/admin/limits', { params });
  },
  
  getUsageStatistics: (params = {}) => {
    return api.get('/mt/admin/statistics', { params });
  },

  getUsersNearLimit: (threshold = 75, params = {}) => {
    return api.get('/mt/admin/users-near-limit', {
      params: { threshold, ...params }
    });
  },
  
  getUserUsageDetails: (userId, months = 6) => {
    return api.get(`/mt/admin/users/${userId}/usage`, {
      params: { months }
    });
  },
  
  updateUserLimit: (userId, monthlyLimit) => {
    return api.put(`/mt/admin/users/${userId}/limit`, {
      monthlyLimit
    });
  },
  
  bulkUpdateLimits: (userIds, monthlyLimit) => {
    return api.post('/mt/admin/bulk-update-limits', {
      userIds,
      monthlyLimit
    });
  },
  
  resetUserUsage: (userId) => {
    return api.post(`/mt/admin/users/${userId}/reset-usage`);
  }
};

// Consistency API endpoints - Context-Aware Translation Feature
// Mounted at /api/consistency on the backend
export const consistencyAPI = {
  // Get consistency issues for a project
  getProjectIssues: (projectId, params = {}) =>
    api.get(`/consistency/projects/${projectId}/issues`, { params }),

  // Get consistency statistics for a project
  getProjectStats: (projectId) =>
    api.get(`/consistency/projects/${projectId}/stats`),

  // Get segments with consistency warnings
  getSegmentsWithWarnings: (projectId, params = {}) =>
    api.get(`/consistency/projects/${projectId}/warnings`, { params }),

  // Detect similar source texts in a project
  detectSimilarTexts: (projectId) =>
    api.get(`/consistency/projects/${projectId}/similar`),

  // Set preferred translation for a consistency record
  setPreferredTranslation: (recordId, preferredTranslation) =>
    api.put(`/consistency/${recordId}/preferred`, { preferredTranslation }),

  // Apply preferred translation to all matching segments
  applyPreferredTranslation: (recordId) =>
    api.post(`/consistency/${recordId}/apply`),

  // Clear consistency warnings for a segment
  clearSegmentWarnings: (segmentId) =>
    api.delete(`/consistency/segments/${segmentId}/warnings`)
};

// Organization API endpoints - Multi-tenant SaaS
export const organizationAPI = {
  // Get available tiers (public)
  getTiers: () => api.get('/organizations/tiers'),

  // User's organizations
  getUserOrganizations: () => api.get('/organizations/my'),
  createOrganization: (data) => api.post('/organizations', data),
  setPrimaryOrganization: (organizationId) =>
    api.put(`/organizations/${organizationId}/primary`),
  acceptInvite: (token) =>
    api.post(`/organizations/invite/${token}/accept`),

  // Current organization context
  getOrganization: () => api.get('/organizations/current'),
  updateOrganization: (data) => api.put('/organizations/current', data),

  // Members management
  getMembers: () => api.get('/organizations/current/members'),
  inviteMember: (data) => api.post('/organizations/current/members/invite', data),
  updateMemberRole: (memberId, role) =>
    api.put(`/organizations/current/members/${memberId}/role`, { role }),
  removeMember: (memberId) =>
    api.delete(`/organizations/current/members/${memberId}`),
  leaveOrganization: () => api.post('/organizations/current/leave'),

  // Usage & Billing
  getUsageSummary: () => api.get('/organizations/current/usage'),
  getUpgradeRecommendation: () => api.get('/organizations/current/upgrade'),
  upgradeTier: (tier) => api.post('/organizations/current/upgrade', { tier }),
  downgradeTier: (tier) => api.post('/organizations/current/downgrade', { tier }),

  // MT Engine Preferences
  getAvailableMTEngines: () => api.get('/organizations/current/mt-engines'),
  getEnabledMTEngines: () => api.get('/organizations/current/mt-engines/enabled'),
  setMTEnginePreference: (engineId, data) => api.put(`/organizations/current/mt-engines/${engineId}`, data),
  disableMTEngine: (engineId) => api.delete(`/organizations/current/mt-engines/${engineId}`),

  // LLM Provider Preferences
  getAvailableLLMProviders: () => api.get('/organizations/current/llm-providers'),
  getEnabledLLMProviders: () => api.get('/organizations/current/llm-providers/enabled'),
  setLLMProviderPreference: (providerId, data) => api.put(`/organizations/current/llm-providers/${providerId}`, data),
  disableLLMProvider: (providerId) => api.delete(`/organizations/current/llm-providers/${providerId}`),

  // Wave-09 · Per-org TM leverage weights (memoQ/Trados analysis).
  // getTmLeverageWeights returns { orgWeights, effective, defaults, bandOrder }
  // setTmLeverageWeights body: { weights: {bandKey: number} | null }
  getTmLeverageWeights: () => api.get('/organizations/current/tm-leverage-weights'),
  setTmLeverageWeights: (weights) => api.put('/organizations/current/tm-leverage-weights', { weights }),
};

// Platform Admin API (Super Admin only)
export const platformAdminAPI = {
  // Platform Statistics
  getStats: () => api.get('/platform/stats'),

  // What the platform paid for instead of customers (chargedTo='platform')
  getPlatformCosts: (months = 6) => api.get('/platform/platform-costs', { params: { months } }),

  // Platform Configuration
  getConfigurations: (category) => api.get('/platform/config', { params: { category } }),
  getConfiguration: (key) => api.get(`/platform/config/${key}`),
  setConfiguration: (data) => api.post('/platform/config', data),
  deleteConfiguration: (key) => api.delete(`/platform/config/${key}`),

  // MT Engine Management
  getMTEngines: () => api.get('/platform/mt-engines'),
  getMTEngine: (id) => api.get(`/platform/mt-engines/${id}`),
  getMTEngineModels: (id) => api.get(`/platform/mt-engines/${id}/models`),
  createMTEngine: (data) => api.post('/platform/mt-engines', data),
  updateMTEngine: (id, data) => api.put(`/platform/mt-engines/${id}`, data),
  deleteMTEngine: (id) => api.delete(`/platform/mt-engines/${id}`),
  testMTEngine: (id) => api.post(`/platform/mt-engines/${id}/test`),

  // LLM Provider Management
  getLLMProviders: () => api.get('/platform/llm-providers'),
  getLLMProvider: (id) => api.get(`/platform/llm-providers/${id}`),
  createLLMProvider: (data) => api.post('/platform/llm-providers', data),
  updateLLMProvider: (id, data) => api.put(`/platform/llm-providers/${id}`, data),
  deleteLLMProvider: (id) => api.delete(`/platform/llm-providers/${id}`),

  // Organization Management (Platform Admin view)
  getAllOrganizations: (params) => api.get('/platform/organizations', { params }),
  getOrganizationDetails: (id) => api.get(`/platform/organizations/${id}`),
  updateOrganization: (id, data) => api.put(`/platform/organizations/${id}`, data),

  // Super-admin quota overrides (bypasses Paddle-driven limits)
  getQuotaOverrides: (id) => api.get(`/platform/organizations/${id}/quota-overrides`),
  setQuotaOverrides: (id, data) => api.put(`/platform/organizations/${id}/quota-overrides`, data),
  clearQuotaOverrides: (id) => api.delete(`/platform/organizations/${id}/quota-overrides`),

  // Super-admin: extend a free-tier org's 6-month lifetime
  extendFreeTier: (id, data) => api.post(`/platform/organizations/${id}/free-tier-extend`, data),

  // LLM live discovery + one-shot probe (populates model dropdown and health pill)
  listAvailableLLMModels: (id, force = false) =>
    api.get(`/platform/llm-providers/${id}/available-models${force ? '?force=1' : ''}`),
  probeLLMProvider: (id) => api.post(`/platform/llm-providers/${id}/probe`)
};

// Admin Analytics API (Super Admin only)
export const adminAnalyticsAPI = {
  // Revenue Analytics
  getRevenueDashboard: (params) => api.get('/admin/analytics/revenue/dashboard', { params }),
  getMRRData: () => api.get('/admin/analytics/revenue/mrr'),
  getChurnData: (params) => api.get('/admin/analytics/revenue/churn', { params }),
  getTrialData: (params) => api.get('/admin/analytics/revenue/trials', { params }),
  getLTVData: () => api.get('/admin/analytics/revenue/ltv'),

  // Usage Monitoring
  getUsageOverview: () => api.get('/admin/analytics/usage/overview'),
  getOrganizationUsage: (params) => api.get('/admin/analytics/usage/organizations', { params }),
  getOrganizationUsageDetails: (orgId, params) => api.get(`/admin/analytics/usage/organization/${orgId}`, { params }),
  updateOrganizationLimits: (orgId, data) => api.put(`/admin/analytics/usage/organization/${orgId}/limits`, data),

  // Customer Management
  getCustomers: (params) => api.get('/admin/analytics/customers', { params }),
  getCustomerDetails: (id) => api.get(`/admin/analytics/customers/${id}`),
  updateCustomerTier: (id, data) => api.put(`/admin/analytics/customers/${id}/tier`, data),
  updateCustomerStatus: (id, data) => api.put(`/admin/analytics/customers/${id}/status`, data)
};

// Payment API endpoints - Multi-provider payment system
export const paymentAPI = {
  // Get available payment providers for organization
  getProviders: () => api.get('/payments/providers'),

  // Create checkout session for subscription
  createCheckout: (data) => api.post('/payments/checkout', data),
  // data = { tier: string, billingPeriod: 'monthly'|'annual', providerName?: string }

  // Create customer portal session for managing subscription
  createPortal: () => api.post('/payments/portal')
};

// PayOS (Vietnam) — VietQR / bank transfer / domestic card
export const payosAPI = {
  getPlans: () => api.get('/billing/payos/plans'),
  createCheckout: (data) => api.post('/billing/payos/checkout', data),
  // data = { tier: string, billingPeriod: 'monthly'|'annual' }
  createAddonCheckout: (data) => api.post('/billing/payos/checkout-addon', data),
  // data = { addonId: string, quantity?: number }
  getOrder: (orderCode) => api.get(`/billing/payos/orders/${orderCode}`),
  // Streams a Vietnamese VAT invoice PDF for a paid order (blob response)
  downloadInvoice: (orderCode) =>
    api.get(`/billing/payos/orders/${orderCode}/invoice.pdf`, { responseType: 'blob' })
};

// Super-admin only: inspect + manually activate PayOS orders.
export const platformPayosAPI = {
  listOrders: (params = {}) => api.get('/platform/payos/orders', { params }),
  // params: { organizationId?, status?, limit? }
  markOrderPaid: (orderCode) => api.post(`/platform/payos/orders/${orderCode}/mark-paid`)
};

// Default export for backward compatibility